import { CartesianChart } from '@/common/dashboard/charts/CartesianChart';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { useEffect, useState } from 'react';

export const ChbkFinTrendChart = ({ formValues, totalValues }) => {

  let { chbkPeriodId, chbkDate, periodicity, distCustIds, ctrtIds, prodIds, agg } = formValues;

  const { totalProdIds, totalDistCustIds, totalCtrtIds } = totalValues;

  const rsrc = useRsrc();

  const { navigateTo } = useNavState();

  const [data, setData] = useState([]);

  const [finalProdIds, setFinalProdIds] = useState([]);
  const [prodIdsNot, setProdIdsNot] = useState(false);
  const [finalDistCustIds, setFinalDistCustIds] = useState([]);
  const [distCustIdsNot, setDistCustIdsNot] = useState(false);
  const [finalCtrtIds, setFinalCtrtIds] = useState([]);
  const [ctrtIdsIsNot, setCtrtIdsIsNot] = useState(false);

  const formatDayCategory = (value) => {
    const raw = String(value ?? '');
    if (raw.length !== 8) {
      return raw;
    }

    const year = raw.substring(0, 4);
    const month = raw.substring(4, 6);
    const day = raw.substring(6, 8);
    return `${month}/${day}/${year}`;
  };

  useEffect(() => {
    if (!chbkPeriodId && !chbkDate) {
      return;
    }

    let prodIdsNot = true;
    if (totalProdIds.length / 2 < prodIds.length) {
      prodIds = totalProdIds.filter(id => !prodIds.includes(id));
    } else {
      prodIdsNot = false;
    }
    setFinalProdIds(prodIds);
    setProdIdsNot(prodIdsNot);

    let distCustIdsNot = true;
    if (totalDistCustIds.length / 2 < distCustIds.length) {
      distCustIds = totalDistCustIds.filter(id => !distCustIds.includes(id));
    } else {
      distCustIdsNot = false;
    }
    setFinalDistCustIds(distCustIds);
    setDistCustIdsNot(distCustIdsNot);

    let ctrtIdsNot = true;
    if (totalCtrtIds.length / 2 < ctrtIds.length) {
      ctrtIds = totalCtrtIds.filter(id => !ctrtIds.includes(id));
    } else {
      ctrtIdsNot = false;
    }
    setFinalCtrtIds(ctrtIds);
    setCtrtIdsIsNot(ctrtIdsNot);

    const reqParam = {
      periodId: chbkPeriodId,
      chbkDate,
      distCustIds,
      distCustIdsNot,
      prodIds,
      prodIdsNot,
      ctrtIds,
      ctrtIdsNot,
      agg
    };

    rsrc.data('sc', 'CHBK_FIN_TREND_CHART_DATA_PLUGIN', reqParam).then(res => {
      setData(res);
    });

  }, [chbkPeriodId, chbkDate, distCustIds, ctrtIds, prodIds, agg]);

  const navigateToClaimLineList = (dataPoint) => {
    const category = dataPoint.category;
    const rowsReq = {};
    if (periodicity === 'MONTH') {
      rowsReq.expr = ExprUtils.whereParams('code', category);
    } else if (periodicity === 'QUARTER') {
      const year = category.substring(0, 4);
      const quarter = category.substring(5);
      const monthStart = (parseInt(quarter) - 1) * 3 + 1;
      const monthPeriodCodes = [monthStart, monthStart + 1, monthStart + 2].map(month => year + 'M' + month.toString().padStart(2, '0'));
      rowsReq.expr = ExprUtils.whereParams('code', monthPeriodCodes);
    } else if (periodicity === 'DAY') {
      const monthPeriodCode = category.substring(0, 4) + 'M' + category.substring(4, 6);
      rowsReq.expr = ExprUtils.whereParams('code', monthPeriodCode);
    }
    rsrc.rows('sc', 'PERIOD', rowsReq).then(periods => {
      const chbkPeriodIds = periods.map(period => period.id);
      let expr = ExprUtils.whereParams('chbkPeriodId', chbkPeriodIds);

      if (finalProdIds.length > 0) {
        let prodExpr;
        if (prodIdsNot) {
          prodExpr = ExprUtils.isNotOneOf('prodId', finalProdIds);
        } else {
          prodExpr = ExprUtils.whereParams('prodId', finalProdIds);
        }
        expr = ExprUtils.and(expr, prodExpr);
      }

      if (finalDistCustIds.length > 0) {
        let distCustExpr;
        if (distCustIdsNot) {
          distCustExpr = ExprUtils.isNotOneOf('chbkClaimPayeeCustId', finalDistCustIds);
        } else {
          distCustExpr = ExprUtils.whereParams('chbkClaimPayeeCustId', finalDistCustIds);
        }
        distCustExpr.fkEntity = 'CHBK_CLAIM';
        distCustExpr.fkColPrefix = 'chbk_claim';
        expr = ExprUtils.and(expr, distCustExpr);
      }

      if (finalCtrtIds.length > 0) {
        let ctrtExpr;
        if (ctrtIdsIsNot) {
          ctrtExpr = ExprUtils.isNotOneOf('ctrtId', finalCtrtIds);
        } else {
          ctrtExpr = ExprUtils.whereParams('ctrtId', finalCtrtIds);
        }
        expr = ExprUtils.and(expr, ctrtExpr);
      }

      if (periodicity === 'DAY') {
        const dateExpr = ExprUtils.whereParams('chbkDate', parseInt(category));
        expr = ExprUtils.and(expr, dateExpr);
      }

      const navState = {
        mode: Mode.LIST,
        expr: expr
      };
      navigateTo('/sc/chbk-claim-line', navState);
    });
  }

  return (
    <>
        <CartesianChart
          data={data}
          yLabel={agg === 'PAID_AMT' ? 'Paid Amount' : 'Paid Quantity'}
          xLabel="Chargeback Period"
          xTickFormatter={periodicity === 'DAY' ? formatDayCategory : undefined}
          allowNegative
          onClick={(dataPoint) => navigateToClaimLineList(dataPoint)}
        />
    </>

  );
};
